import {createContext, useContext, useRef} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'
import {createCodeownersSlice, type CodeownersSlice} from './slices/codeowners'
import {
  createComparisonInfoSlice,
  type ComparisonInfoSlice,
  type ComparisonInfoSliceOpts,
} from './slices/comparison-info'
import {createDiffFilterSlice, type DiffFilterSlice} from './slices/diff-filter'
import {createDiffSummariesSlice, type DiffSummariesSlice, type DiffSummariesSliceOpts} from './slices/diff-summaries'
import {createMarkersSlice, type MarkersSlice, type MarkersSliceOpts} from './slices/markers'
import {
  createProgressiveDiffsSlice,
  type ProgressiveDiffsSlice,
  type ProgressiveDiffSliceOpts,
} from './slices/progressive-diffs'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import {getSelectedFullDiffHash, parsePathDigestWithoutLineNumbers} from '@github-ui/diffs/document-hash-helpers'
import {useWhitespacePreference} from '@github-ui/diff-view-settings/page-data/payloads/diff-view-settings'
import {useSelectedRefContext} from '../contexts/SelectedRefContext'
import {
  createFilesCollapsedStatusSlice,
  type FilesCollapsedStatusSlice,
  type FilesCollapsedStatusSliceOpts,
} from './slices/files-collapsed-status'
import {
  buildCollapsedDiffStatusMap,
  buildDiffEntryMap,
  buildDiffSummaryMap,
  buildNormalizedMarkers,
  buildPendingReview,
} from '../helpers/diff-helpers'
import type {DiffEntry} from '@github-ui/diff-lines'
import {createDiffEntriesSlice, type DiffEntriesSlice, type DiffEntriesSliceOpts} from './slices/diff-entries'
import {diffEntriesKey} from '../page-data/loaders/use-diff-entries'
import {getQueryClient} from '@github-ui/react-core/query-client'
import {createPendingReviewSlice, type PendingReviewSlice, type PendingReviewSliceOpts} from './slices/pending-review'
import {
  createMarkerAndDiffNavigationSlice,
  type MarkerAndDiffNavigationOpts,
  type MarkerAndDiffNavigationSlice,
} from './slices/marker-and-diff-navigation'
import {createSettingsSlice, type SettingsSlice, type SettingsSliceOpts} from './slices/settings'
import {createPullRequestSlice, type PullRequestSlice, type PullRequestSliceOpts} from './slices/pull-request'

type InitialState = ComparisonInfoSliceOpts &
  ProgressiveDiffSliceOpts &
  MarkersSliceOpts &
  FilesCollapsedStatusSliceOpts &
  PendingReviewSliceOpts &
  DiffEntriesSliceOpts &
  DiffSummariesSliceOpts &
  MarkerAndDiffNavigationOpts &
  SettingsSliceOpts &
  PullRequestSliceOpts
export type State = CodeownersSlice &
  ComparisonInfoSlice &
  DiffFilterSlice &
  DiffSummariesSlice &
  MarkersSlice &
  ProgressiveDiffsSlice &
  FilesCollapsedStatusSlice &
  PendingReviewSlice &
  DiffEntriesSlice &
  MarkerAndDiffNavigationSlice &
  SettingsSlice &
  PullRequestSlice
type PullRequestStore = ReturnType<typeof createPullRequestStore>

const PullRequestStoreContext = createContext<PullRequestStore | null>(null)

export function usePullRequestStore<T>(selector: (state: State) => T): T {
  const store = useContext(PullRequestStoreContext)
  if (!store) throw new Error('Missing PullRequestStoreProvider in the tree')
  return useStore(store, selector)
}

export const PullRequestStoreProvider = ({children}: React.PropsWithChildren) => {
  const storeRef = useRef<PullRequestStore | null>(null)
  const prxFilesLiteThrottle = useFeatureFlag('prx_files_lite_throttle')
  const prxFilesMediumThrottle = useFeatureFlag('prx_files_medium_throttle')
  const routeData = useRoutePayload<FilesRoutePayload>()
  const hideWhitespace = useWhitespacePreference(routeData.user.viewSettings.hideWhitespace)
  const urlCommitRange = useSelectedRefContext()
  const diffSummaryMap = buildDiffSummaryMap(routeData.diffSummaries)
  // this is also used as the initial state for all viewed files
  const collapsedFilesMap = buildCollapsedDiffStatusMap(routeData.diffSummaries)
  const pendingReview = buildPendingReview(routeData.viewerPendingReview)
  const initialDiffEntryMap = buildDiffEntryMap(routeData.diffContents as DiffEntry[])
  const normalizedMarkers = buildNormalizedMarkers(routeData.markers, routeData.diffSummaries)

  if (!storeRef.current) {
    populateTanstackDiffEntryCache(
      urlCommitRange.startOid,
      urlCommitRange.endOid,
      routeData.pullRequest.pathName,
      routeData.diffContents as DiffEntry[],
      routeData.pullRequest.comparison,
      hideWhitespace,
    )
    storeRef.current = createPullRequestStore({
      baseOid: routeData.pullRequest.comparison.baseOid,
      headOid: routeData.pullRequest.comparison.headOid,
      selectedCommitRange: {
        baseOid: urlCommitRange.startOid ?? undefined,
        headOid: urlCommitRange.endOid ?? undefined,
      },
      hideWhitespace,
      isSingleFileMode: routeData.isSingleFileMode,
      collapsedFilesMap,
      initialSummariesViewedStatus: collapsedFilesMap,
      initialSummariesViewedCount: routeData.user.viewedFilesCount,
      diffEntries: initialDiffEntryMap,
      initialDiffSummaries: diffSummaryMap,
      initialLoadedPathDigests: new Set(routeData.diffContents.map(entry => entry.pathDigest)),
      markers: normalizedMarkers,
      basePath: routeData.pullRequest.pathName,
      pendingReview,
      prxFilesLiteThrottle,
      prxFilesMediumThrottle,
      pullRequest: routeData.pullRequest,
      selectedPathDigest: parsePathDigestWithoutLineNumbers(getSelectedFullDiffHash()),
    })
  }

  return <PullRequestStoreContext.Provider value={storeRef.current}>{children}</PullRequestStoreContext.Provider>
}

const createPullRequestStore = (initState: InitialState) => {
  return createStore<State>()(
    devtools(
      (...a) => ({
        ...createCodeownersSlice()(...a),
        ...createComparisonInfoSlice(initState)(...a),
        ...createDiffFilterSlice()(...a),
        ...createDiffSummariesSlice(initState)(...a),
        ...createMarkersSlice(initState)(...a),
        ...createProgressiveDiffsSlice(initState)(...a),
        ...createFilesCollapsedStatusSlice(initState)(...a),
        ...createPendingReviewSlice(initState)(...a),
        ...createDiffEntriesSlice(initState)(...a),
        ...createMarkerAndDiffNavigationSlice(initState)(...a),
        ...createSettingsSlice(initState)(...a),
        ...createPullRequestSlice(initState)(...a),
      }),
      {name: 'pull-request-store'},
    ),
  )
}

function populateTanstackDiffEntryCache(
  startOid: string | null | undefined,
  endOid: string | null | undefined,
  basePath: string,
  diffEntries: DiffEntry[],
  comparison: {
    baseOid: string
    headOid: string
  },
  hideWhitespace: boolean,
): void {
  const queryClient = getQueryClient()
  const queryKey = diffEntriesKey({
    basePath,
    selectedRange: {
      baseOid: startOid ?? comparison.baseOid,
      headOid: endOid ?? comparison.headOid,
    },
    hideWhitespace,
  })
  queryClient.setQueryData(queryKey, diffEntries)
}

try{ PullRequestStoreContext.displayName ||= 'PullRequestStoreContext' } catch {}
try{ PullRequestStoreProvider.displayName ||= 'PullRequestStoreProvider' } catch {}